import { useTranslation } from "react-i18next";
import cn from "classnames";
import { CATEGORY_CONFIG } from "../consts/categories";
import type { Notification } from "../types";

interface Props {
  notification: Notification;
  onClick: (notification: Notification) => void;
}

function formatRelativeTime(dateStr: string, locale: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  if (days > 0) return rtf.format(-days, "day");
  if (hours > 0) return rtf.format(-hours, "hour");
  if (minutes > 0) return rtf.format(-minutes, "minute");
  return rtf.format(0, "minute");
}

export default function NotificationItem({ notification, onClick }: Props) {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const categoryConfig = CATEGORY_CONFIG[notification.category];

  return (
    <button
      type="button"
      className={cn(
        "w-full text-left not-last:mb-1 p-3 radius-md flex gap-3 items-start transition-colors",
        !notification.is_read ? "surface-component-fill" : "surface-base-fill",
      )}
      onClick={() => onClick(notification)}>
      <div
        className={cn(
          "shrink-0 w-10 aspect-square radius-sm flex justify-center items-center",
          categoryConfig.tileClass,
        )}>
        <img src={categoryConfig.image} alt="Notification" className={categoryConfig.imageClass} />
      </div>

      <div className="flex-1 gap-0.5 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="text-label-md content-base-primary truncate">{notification.title}</p>
          <div className="flex justify-between items-center gap-2">
            <p className="text-body-regular-sm content-base-low truncate">
              {formatRelativeTime(notification.created_at, locale)}
            </p>
            {!notification.is_read && <span className="shrink-0 w-1.5 h-1.5 radius-full surface-brand-fill" />}
          </div>
        </div>
        <p className="text-body-regular-sm content-base-secondary line-clamp-2">{notification.body}</p>
      </div>
    </button>
  );
}
